import type {
  SoundListResponse,
  SoundResponse,
  TagResponse,
  TrendingResponse,
  FeaturedResponse,
  SearchSuggestResponse,
  AnalyticsOverviewResponse,
  CountryBreakdownResponse,
  DeviceBreakdownResponse,
  RegionBreakdownResponse,
  HourlyHeatmapResponse,
  DailyListenersResponse,
  FeaturedHistoryResponse,
  PublicStatsResponse,
  SoundAnalyticsResponse,
  AiAnalyticsResponse,
  WebhookResponse,
  UploadUrlResponse,
  BoardsResponse,
  BoardResponse,
  AuditLogResponse,
  ComboResponse,
  ComboListResponse,
  ComboCreateRequest,
} from "@wizard-sounds/shared";
import type { SortOption } from "@wizard-sounds/shared";

const BASE = "/api";

async function fetchJSON<T>(url: string, init?: RequestInit & { timeout?: number }): Promise<T> {
  const { timeout, ...fetchInit } = init ?? {};
  if (timeout) {
    fetchInit.signal = AbortSignal.timeout(timeout);
  }
  const res = await fetch(url, fetchInit);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(body.error || body.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  sounds: {
    list(params?: {
      q?: string;
      tags?: string;
      sort?: SortOption;
      limit?: number;
      cursor?: string;
    }) {
      const sp = new URLSearchParams();
      if (params?.q) sp.set("q", params.q);
      if (params?.tags) sp.set("tags", params.tags);
      if (params?.sort) sp.set("sort", params.sort);
      if (params?.limit) sp.set("limit", String(params.limit));
      if (params?.cursor) sp.set("cursor", params.cursor);
      return fetchJSON<SoundListResponse>(`${BASE}/sounds?${sp}`);
    },
    get(slug: string) {
      return fetchJSON<SoundResponse>(`${BASE}/sounds/${slug}`);
    },
    random() {
      return fetchJSON<SoundResponse>(`${BASE}/sounds/random`);
    },
    play(id: string) {
      return fetch(`${BASE}/sounds/${id}/play`, { method: "POST" });
    },
  },

  tags: {
    list() {
      return fetchJSON<TagResponse[]>(`${BASE}/tags`);
    },
  },

  trending() {
    return fetchJSON<TrendingResponse>(`${BASE}/trending`);
  },

  featured() {
    return fetchJSON<FeaturedResponse>(`${BASE}/featured`);
  },

  boards() {
    return fetchJSON<BoardsResponse>(`${BASE}/boards`);
  },

  stats() {
    return fetchJSON<PublicStatsResponse>(`${BASE}/stats`);
  },

  combos: {
    list(params?: { sort?: string; limit?: number; cursor?: string }) {
      const sp = new URLSearchParams();
      if (params?.sort) sp.set("sort", params.sort);
      if (params?.limit) sp.set("limit", String(params.limit));
      if (params?.cursor) sp.set("cursor", params.cursor);
      return fetchJSON<ComboListResponse>(`${BASE}/combos?${sp}`);
    },
    get(slug: string) {
      return fetchJSON<ComboResponse>(`${BASE}/combos/${slug}`);
    },
    create(body: ComboCreateRequest) {
      return fetchJSON<ComboResponse>(`${BASE}/combos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    play(id: string) {
      return fetch(`${BASE}/combos/${id}/play`, { method: "POST" });
    },
  },

  searchSuggest(q: string) {
    return fetchJSON<SearchSuggestResponse>(
      `${BASE}/search/suggest?q=${encodeURIComponent(q)}`,
    );
  },

  admin: {
    me() {
      return fetchJSON<{ email: string | null; name: string | null }>(`${BASE}/admin/me`);
    },
    getUploadUrl() {
      return fetchJSON<UploadUrlResponse>(`${BASE}/admin/sounds/upload-url`, {
        method: "POST",
      });
    },
    transcribe(file: Blob) {
      return fetchJSON<{ text: string }>(`${BASE}/admin/sounds/transcribe`, {
        method: "POST",
        headers: { "Content-Type": file.type || "audio/mpeg" },
        body: file,
        timeout: 35_000,
      });
    },
    transcribeById(id: string) {
      return fetchJSON<{ text: string }>(`${BASE}/admin/sounds/${id}/transcribe`, {
        method: "POST",
        timeout: 35_000,
      });
    },
    aiSuggest(body: { title: string; transcript: string }) {
      return fetchJSON<{ description: string; tags: string[] }>(`${BASE}/admin/sounds/ai-suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        timeout: 20_000,
      });
    },
    listSounds(params?: {
      q?: string;
      tags?: string;
      sort?: string;
      status?: string;
      limit?: number;
      offset?: number;
    }) {
      const sp = new URLSearchParams();
      if (params?.q) sp.set("q", params.q);
      if (params?.tags) sp.set("tags", params.tags);
      if (params?.sort) sp.set("sort", params.sort);
      if (params?.status) sp.set("status", params.status);
      if (params?.limit) sp.set("limit", String(params.limit));
      if (params?.offset) sp.set("offset", String(params.offset));
      return fetchJSON<{ sounds: SoundResponse[]; total: number; limit: number; offset: number }>(
        `${BASE}/admin/sounds?${sp}`,
      );
    },
    createSound(body: Record<string, unknown>) {
      return fetchJSON<SoundResponse>(`${BASE}/admin/sounds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    updateSound(id: string, body: Record<string, unknown>) {
      return fetchJSON<SoundResponse>(`${BASE}/admin/sounds/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    pinSound(id: string, pinned: boolean, pinOrder?: number) {
      return fetchJSON<SoundResponse>(`${BASE}/admin/sounds/${id}/pin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_pinned: pinned ? 1 : 0, pin_order: pinOrder }),
      });
    },
    archiveSound(id: string) {
      return fetchJSON<SoundResponse>(`${BASE}/admin/sounds/${id}/archive`, {
        method: "PATCH",
      });
    },
    deleteSound(id: string) {
      return fetch(`${BASE}/admin/sounds/${id}`, { method: "DELETE" });
    },
    bulkDelete(soundIds: string[]) {
      return fetchJSON<{ ok: boolean; deleted: number }>(`${BASE}/admin/sounds/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sound_ids: soundIds }),
      });
    },
    bulkArchive(soundIds: string[], archive: boolean) {
      return fetchJSON<{ ok: boolean; count: number }>(`${BASE}/admin/sounds/bulk-archive`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sound_ids: soundIds, archive }),
      });
    },
    bulkTags(body: { sound_ids: string[]; add_tags?: string[]; remove_tags?: string[] }) {
      return fetchJSON(`${BASE}/admin/sounds/bulk-tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    backupJson() {
      return `${BASE}/admin/backup`;
    },
    backupZip() {
      return `${BASE}/admin/backup/zip`;
    },
    backupsList() {
      return fetchJSON<{
        files: Array<{
          key: string;
          tier: "weekly" | "monthly";
          size: number;
          uploaded: string;
          sound_count: string | null;
          combo_count: string | null;
          board_count: string | null;
        }>;
      }>(`${BASE}/admin/backups/list`);
    },
    backupDownloadUrl(key: string) {
      return `${BASE}/admin/backups/download?key=${encodeURIComponent(key)}`;
    },
    restore(file: File) {
      const fd = new FormData();
      fd.append("file", file);
      return fetchJSON<{
        sounds_imported: number;
        sounds_skipped: number;
        combos_imported: number;
        combos_skipped: number;
        boards_imported: number;
        boards_skipped: number;
        errors: string[];
      }>(`${BASE}/admin/restore`, { method: "POST", body: fd });
    },
    analyticsOverview(days?: number) {
      const sp = days ? `?days=${days}` : "";
      return fetchJSON<AnalyticsOverviewResponse>(`${BASE}/admin/analytics/overview${sp}`);
    },
    analyticsCountries(days?: number) {
      const sp = days ? `?days=${days}` : "";
      return fetchJSON<CountryBreakdownResponse>(`${BASE}/admin/analytics/countries${sp}`);
    },
    analyticsRegions(country?: string, days?: number) {
      const sp = new URLSearchParams();
      if (country) sp.set("country", country);
      if (days) sp.set("days", String(days));
      const qs = sp.toString();
      return fetchJSON<RegionBreakdownResponse>(`${BASE}/admin/analytics/regions${qs ? `?${qs}` : ""}`);
    },
    analyticsDevices(days?: number) {
      const sp = days ? `?days=${days}` : "";
      return fetchJSON<DeviceBreakdownResponse>(`${BASE}/admin/analytics/devices${sp}`);
    },
    analyticsHourly(days?: number) {
      const sp = days ? `?days=${days}` : "";
      return fetchJSON<HourlyHeatmapResponse>(`${BASE}/admin/analytics/hourly${sp}`);
    },
    analyticsListeners(days?: number) {
      const sp = days ? `?days=${days}` : "";
      return fetchJSON<DailyListenersResponse>(`${BASE}/admin/analytics/listeners${sp}`);
    },
    analyticsFeaturedHistory(limit?: number) {
      const sp = limit ? `?limit=${limit}` : "";
      return fetchJSON<FeaturedHistoryResponse>(`${BASE}/admin/analytics/featured-history${sp}`);
    },
    soundAnalytics(id: string) {
      return fetchJSON<SoundAnalyticsResponse>(`${BASE}/admin/analytics/sound/${id}`);
    },
    analyticsAi(days?: number) {
      const sp = days ? `?days=${days}` : "";
      return fetchJSON<AiAnalyticsResponse>(`${BASE}/admin/analytics/ai${sp}`);
    },
    boards: {
      list() {
        return fetchJSON<BoardResponse[]>(`${BASE}/admin/boards`);
      },
      create(body: Record<string, unknown>) {
        return fetchJSON<BoardResponse>(`${BASE}/admin/boards`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      },
      update(id: string, body: Record<string, unknown>) {
        return fetchJSON<BoardResponse>(`${BASE}/admin/boards/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      },
      delete(id: string) {
        return fetch(`${BASE}/admin/boards/${id}`, { method: "DELETE" });
      },
    },
    combos: {
      list(params?: { limit?: number; offset?: number }) {
        const sp = new URLSearchParams();
        if (params?.limit) sp.set("limit", String(params.limit));
        if (params?.offset) sp.set("offset", String(params.offset));
        return fetchJSON<{ combos: ComboResponse[]; total: number; limit: number; offset: number }>(
          `${BASE}/admin/combos?${sp}`,
        );
      },
      update(id: string, body: { name?: string; description?: string; tags?: string[] }) {
        return fetchJSON<ComboResponse>(`${BASE}/admin/combos/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      },
      delete(id: string) {
        return fetch(`${BASE}/admin/combos/${id}`, { method: "DELETE" });
      },
      aiSuggest(body: { name?: string; sounds: { title: string; transcript?: string }[] }) {
        return fetchJSON<{ name: string; description: string; tags: string[] }>(`${BASE}/admin/combos/ai-suggest`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          timeout: 20_000,
        });
      },
    },
    audit(params?: { limit?: number; offset?: number; action?: string; entity_type?: string; from?: number; to?: number }) {
      const sp = new URLSearchParams();
      if (params?.limit) sp.set("limit", String(params.limit));
      if (params?.offset) sp.set("offset", String(params.offset));
      if (params?.action) sp.set("action", params.action);
      if (params?.entity_type) sp.set("entity_type", params.entity_type);
      if (params?.from) sp.set("from", String(params.from));
      if (params?.to) sp.set("to", String(params.to));
      return fetchJSON<AuditLogResponse>(`${BASE}/admin/audit?${sp}`);
    },
    webhooks: {
      list() {
        return fetchJSON<WebhookResponse[]>(`${BASE}/admin/webhooks`);
      },
      create(body: Record<string, unknown>) {
        return fetchJSON<WebhookResponse>(`${BASE}/admin/webhooks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      },
      update(id: string, body: Record<string, unknown>) {
        return fetchJSON<WebhookResponse>(`${BASE}/admin/webhooks/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      },
      delete(id: string) {
        return fetch(`${BASE}/admin/webhooks/${id}`, { method: "DELETE" });
      },
    },
    maintenance: {
      status() {
        return fetchJSON<{
          jobs: { name: string; schedule: string; description: string }[];
          runs: {
            id: number;
            job_name: string;
            started_at: number;
            finished_at: number | null;
            status: string;
            result: string | null;
            items_deleted: number;
            items_checked: number;
          }[];
          r2_stats: { total_objects: number; referenced: number; orphaned: number };
        }>(`${BASE}/admin/maintenance`);
      },
      runCleanup() {
        return fetchJSON<{ deleted: number; checked: number }>(`${BASE}/admin/maintenance/cleanup`, {
          method: "POST",
        });
      },
      runJob(jobName: string) {
        return fetchJSON<{ deleted: number; checked: number }>(`${BASE}/admin/maintenance/run`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ job: jobName }),
        });
      },
      runAll() {
        return fetchJSON<{ ok: boolean }>(`${BASE}/admin/maintenance/run`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
      },
    },
  },
};
